/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback, useRef } from 'preact/hooks';

type CallbackFunction = (event: Event) => void;

type UseDoubleTapProps = {
  isSingleTapImmediate?: boolean;
  onDoubleTap: CallbackFunction;
  onSingleTap?: CallbackFunction;
  threshold?: number;
};

/**
 * A hook that supports double tap on mobile. You can optionally pass an onSingleTap handler
 * which is called if the user taps once, but doesn't tap again within the threshold waiting
 * period (isSingleTapImmediate = false). If isSingleTapImmediate is true, onSingleTap is
 * invoked immediately after the first tap.
 *
 * If the user taps again within the threshold wait period, then onDoubleTap is called.
 */
export function useDoubleTap({
  isSingleTapImmediate = true,
  onDoubleTap,
  onSingleTap,
  threshold = 400
}: UseDoubleTapProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handler = useCallback(
    (event: MouseEvent) => {
      if (!timerRef.current) {
        isSingleTapImmediate && onSingleTap?.(event);
        timerRef.current = setTimeout(() => {
          !isSingleTapImmediate && onSingleTap?.(event);
          timerRef.current = null;
        }, threshold);
      } else {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        onDoubleTap?.(event);
      }
    },
    [isSingleTapImmediate, onDoubleTap, onSingleTap, threshold]
  );

  return { onClick: handler };
}
